import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getOrderConfirmation, guestSyncCart } from '../lib/api';
import { trackEvent } from '../lib/metaPixel';
import { getPaymentLabel, getPaymentReferenceId, getShippingMethodDescription } from '../lib/orderDisplay';
import MapPreview from '../components/MapPreview';
import './OrderConfirmationPage.css';

function CheckCircleIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12.5 11 15.5 16 9" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v16H4z" /><path d="M22 6l-10 7L2 6" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function TruckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

export default function OrderConfirmationPage({ user, clearCart }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [fetchedOrder, setFetchedOrder] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [fetching, setFetching] = useState(false);

  const stateOrder = location.state?.order;
  const redirectOrderId = searchParams.get('orderId');

  // PayU sends the customer's browser back via a real navigation (no React
  // Router state survives that), so when we land here with only ?orderId=
  // we fetch the order instead.
  useEffect(() => {
    if (stateOrder || !redirectOrderId) return;
    setFetching(true);
    getOrderConfirmation(redirectOrderId)
      .then(setFetchedOrder)
      .catch(() => setFetchError(true))
      .finally(() => setFetching(false));
  }, [stateOrder, redirectOrderId]);

  // PayU is a full-page redirect, so the cart can't be cleared from
  // CheckoutPage before the customer ever reaches PayU (if the payment then
  // fails, they're sent back to /checkout and need their cart intact). It's
  // safe to clear it here instead, now that we know the payment actually went through.
  useEffect(() => {
    if (fetchedOrder?.payment === 'payu' && fetchedOrder?.paymentStatus === 'paid' && clearCart) {
      clearCart();
      if (!user && fetchedOrder.address?.email) {
        guestSyncCart({ items: [], email: fetchedOrder.address.email }).catch(() => {});
      }
    }
  }, [fetchedOrder, clearCart, user]);

  const order = stateOrder || fetchedOrder;

  // Fire once per successfully confirmed order. Placed above the early
  // returns below (must run every render, per Rules of Hooks) and guarded
  // internally instead — both for "order not loaded yet" and for
  // re-renders/PayU refetches of the same order.
  const trackedOrderIdRef = useRef(null);
  useEffect(() => {
    if (!order || order.paymentStatus === 'failed') return;
    const oid = order.orderId;
    if (!oid || trackedOrderIdRef.current === oid) return;
    trackedOrderIdRef.current = oid;
    const items = order.items || [];
    trackEvent('Purchase', {
      content_ids: items.map(i => String(i.productId || i._id || i.id || '')),
      contents: items.map(i => ({ id: String(i.productId || i._id || i.id || ''), quantity: i.qty })),
      content_type: 'product',
      num_items: items.reduce((s, i) => s + (i.qty || 0), 0),
      value: order.finalTotal || 0,
      currency: 'INR',
      order_id: oid,
    });
  }, [order]);

  if (!order) {
    if (fetching) {
      return (
        <main className="ocp-page">
          <div className="ocp-empty">
            <h1>Confirming your payment…</h1>
            <p>This will only take a moment.</p>
          </div>
        </main>
      );
    }
    if (redirectOrderId && fetchError) {
      return (
        <main className="ocp-page">
          <div className="ocp-empty">
            <h1>We couldn't confirm that order</h1>
            <p>If your payment was successful, you'll find it under My Orders shortly. If money was deducted and it doesn't show up, please contact support.</p>
            <Link to="/orders" className="ocp-empty__link">View my orders →</Link>
          </div>
        </main>
      );
    }
    return (
      <main className="ocp-page">
        <div className="ocp-empty">
          <h1>No order to show</h1>
          <p>We couldn't find any recent order details to display.</p>
          <Link to="/collections/plants" className="ocp-empty__link">Continue shopping →</Link>
        </div>
      </main>
    );
  }

  if (order.paymentStatus === 'failed') {
    return (
      <main className="ocp-page">
        <div className="ocp-empty">
          <h1>Payment wasn't completed</h1>
          <p>Your payment didn't go through, so this order hasn't been placed. Please return to checkout to try again.</p>
          <Link to="/checkout" className="ocp-empty__link">Back to checkout →</Link>
        </div>
      </main>
    );
  }

  const {
    orderId, items = [], subtotal = 0, discount = 0, shipping = 0, codFee = 0,
    finalTotal = 0, promoCode, address = {}, deliveryCoords, payment,
    createdAt,
  } = order;

  const placedOn = createdAt
    ? new Date(createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const paymentLabel = getPaymentLabel(order);
  const paymentReference = getPaymentReferenceId(order);
  const shippingMethodDescription = getShippingMethodDescription(order);

  const fullName = `${address.firstName || ''} ${address.lastName || ''}`.trim();

  return (
    <main className="ocp-page">
      <div className="ocp-inner">

        {/* ── Left column ── */}
        <div className="ocp-left">

          <div className="ocp-hero">
            <div className="ocp-hero__check"><CheckCircleIcon /></div>
            <p className="ocp-hero__conf">Confirmation #{orderId}</p>
            <h1 className="ocp-hero__title">Thank you{fullName ? `, ${fullName.split(' ')[0]}` : ''}!</h1>
            {placedOn && <p className="ocp-hero__date">Placed on {placedOn}</p>}
          </div>

          <section className="ocp-card">
            <h2 className="ocp-card__title">Your order is confirmed</h2>
            <p className="ocp-card__sub">You'll receive a confirmation {address.email ? 'email' : 'text'} soon.</p>
          </section>

          <section className="ocp-card">
            <h2 className="ocp-card__title">Order updates</h2>
            <p className="ocp-card__sub">You'll get shipping and delivery updates {address.email ? 'by email' : 'by SMS'}.</p>
            <div className="ocp-card__row">
              <span className="ocp-pill"><TruckIcon /> {shippingMethodDescription}</span>
            </div>
          </section>

          {deliveryCoords && deliveryCoords.lat != null && deliveryCoords.lng != null && (
            <section className="ocp-card">
              <h2 className="ocp-card__title">Delivery location</h2>
              <p className="ocp-card__sub">Your plant will arrive at the pinned location below.</p>
              <div className="ocp-map-wrap">
                <MapPreview lat={deliveryCoords.lat} lng={deliveryCoords.lng} />
              </div>
              <p className="ocp-map-address"><PinIcon /> {address.address}{address.apartment ? `, ${address.apartment}` : ''}, {address.city}, {address.state} {address.pincode}</p>
            </section>
          )}

          <section className="ocp-card">
            <h2 className="ocp-card__title">Order details</h2>
            <div className="ocp-details-grid">
              <div className="ocp-details-col">
                <h3 className="ocp-details-label">Contact information</h3>
                <p className="ocp-details-text">{address.email || address.phone}</p>

                <h3 className="ocp-details-label">Shipping address</h3>
                <p className="ocp-details-text">
                  {fullName}<br />
                  {address.address}{address.apartment ? `, ${address.apartment}` : ''}<br />
                  {address.city}, {address.state} {address.pincode}<br />
                  India<br />
                  {address.phone}
                </p>

                <h3 className="ocp-details-label">Shipping method</h3>
                <p className="ocp-details-text">{shippingMethodDescription}</p>
              </div>
              <div className="ocp-details-col">
                <h3 className="ocp-details-label">Payment method</h3>
                <p className="ocp-details-text">{paymentLabel}</p>
                {paymentReference && (
                  <>
                    <h3 className="ocp-details-label">Payment reference</h3>
                    <p className="ocp-details-text ocp-details-text--mono">{paymentReference}</p>
                  </>
                )}

                <h3 className="ocp-details-label">Billing address</h3>
                <p className="ocp-details-text">
                  {fullName}<br />
                  {address.address}{address.apartment ? `, ${address.apartment}` : ''}<br />
                  {address.city}, {address.state} {address.pincode}<br />
                  India
                </p>
              </div>
            </div>
          </section>

          <div className="ocp-actions">
            {user ? (
              <button className="ocp-btn ocp-btn--primary" onClick={() => navigate(`/account/orders/${orderId}`)}>
                View order
              </button>
            ) : null}
            <button className="ocp-btn" onClick={() => navigate('/collections/plants')}>
              Continue shopping
            </button>
          </div>
        </div>

        {/* ── Right column / summary ── */}
        <aside className="ocp-right">
          <div className="ocp-summary">
            <div className="ocp-summary__items">
              {items.map((item, idx) => {
                const imgSrc = item.image || item.imageUrl || '';
                const isBundle = item.itemType === 'bundle';
                return (
                  <div key={idx} className="ocp-summary__item">
                    <div className="ocp-summary__img-wrap">
                      <span className="ocp-summary__badge">{item.qty}</span>
                      <div className="ocp-summary__img">
                        {imgSrc
                          ? <img src={imgSrc} alt={item.name}
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          : null}
                        <div className="ocp-summary__img-fallback" style={imgSrc ? { display: 'none' } : {}}>🌿</div>
                      </div>
                    </div>
                    <div className="ocp-summary__item-body">
                      <p className="ocp-summary__item-name">
                        {item.name}
                        {isBundle && <span className="ocp-bundle-badge">Bundle</span>}
                      </p>
                      {(item.selectedSize || item.selectedPlanter || item.selectedColor) && (
                        <p className="ocp-summary__item-variants">
                          {[item.selectedSize, item.selectedPlanter, item.selectedColor].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {/* Bundle: show individual products with variant image + details */}
                      {isBundle && item.bundleItems && item.bundleItems.length > 0 && (
                        <div className="ocp-bundle-plants">
                          <p className="ocp-bundle-plants__label">Includes:</p>
                          {item.bundleItems.map((bi, i) => {
                            const biVariants = [bi.selectedSize, bi.selectedPlanter, bi.selectedColor].filter(Boolean);
                            return (
                              <div key={i} className="ocp-bundle-plant">
                                <div className="ocp-bundle-plant__img-wrap">
                                  {bi.image
                                    ? <img src={bi.image} alt={bi.name} className="ocp-bundle-plant__img"
                                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                    : null}
                                  <div className="ocp-bundle-plant__fallback" style={bi.image ? { display: 'none' } : {}}>🌿</div>
                                </div>
                                <div className="ocp-bundle-plant__details">
                                  <span className="ocp-bundle-plant__name">{bi.name}</span>
                                  {biVariants.length > 0 && (
                                    <span className="ocp-bundle-plant__variants">{biVariants.join(' · ')}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <p className="ocp-summary__item-price">₹{(item.price * item.qty).toLocaleString()}</p>
                  </div>
                );
              })}
            </div>

            <div className="ocp-divider" />

            <div className="ocp-summary__lines">
              <div className="ocp-summary__line">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>
              {discount > 0 && (
                <div className="ocp-summary__line ocp-summary__line--disc">
                  <span>Discount{promoCode ? ` (${promoCode})` : ''}</span>
                  <span>−₹{discount.toLocaleString()}</span>
                </div>
              )}
              <div className="ocp-summary__line">
                <span>Shipping</span>
                <span>{shipping === 0 ? <span className="ocp-free">Free</span> : `₹${shipping}`}</span>
              </div>
              {codFee > 0 && (
                <div className="ocp-summary__line">
                  <span>Cash on Delivery fee</span>
                  <span>₹{codFee.toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="ocp-divider" />

            <div className="ocp-summary__total">
              <div>
                <span className="ocp-summary__total-label">Total</span>
                <span className="ocp-summary__currency">INR</span>
              </div>
              <span className="ocp-summary__total-amount">₹{finalTotal.toLocaleString()}</span>
            </div>
          </div>
        </aside>

      </div>
    </main>
  );
}